import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { QUERY_KEYS } from 'constants/constants'
import PersonHeart from '../../../assets/images/illustrations/person-heart.png'
import { usePartnerContext } from '../../../context'
import {
  ARCHIVED_CAREER_ACTIONS,
  ARCHIVED_CAREER_HINTS,
  ARCHIVED_CAREER_RESTORE,
  getSpaceCareerId,
  isSpaceOfArchivedCareer,
  RECOVER_CAREERS_PATH
} from '../../../features/pricing/archived-careers'
import { query } from '../../../graphql'
import {
  computeExpiresAt,
  getExpiresAfterDays,
  laterExpiresAt
} from '../../../helpers/assessment-deadline'
import { useArchivedCareerIds } from '../../../hooks/use-archived-career-ids'
import { useMailTemplates } from '../../../hooks/use-mail-templates'
import EditMailTemplate from '../../mail-template/edit-mail-template'
import SelectMenu from '../../select-menu'
import Button from '../../tailwind/Button'
import InfoCallout from '../../tailwind/InfoCallout'
import Modal from '../../tailwind/Modal'
import SpaceInviteLink from '../../talents/space-invite-link'

const ReinviteSpaceModal = ({ space = {}, updateHandler, open, setOpen }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()

  const space_has_email = !!(
    space.person?.email || space.person?.invitation_email
  )

  const [state, setState] = useState('INITIAL')
  const [context, setContext] = useState(space_has_email ? 'EMAIL' : 'LINK')
  const [showMailTemplatePreview, setShowMailTemplatePreview] = useState(false)

  // Die Freischaltung verlängert die Frist wie inviteTalent
  // (getExtendedExpiresAt) um die Bearbeitungszeit der Stelle und kürzt nie.
  // Die Stelle geht deshalb nur in das Fristende der Vorschau ein, als career
  // würde sie die Crossmode-Vorlage durch die Vorlage der Stelle ersetzen.
  const careerId = getSpaceCareerId(space)
  const { data: career } = useQuery({
    queryKey: QUERY_KEYS.careerById(careerId),
    queryFn: () => query({ query: 'getCareer', variables: { id: careerId } }),
    enabled: open && !!careerId
  })
  const expiresAt = laterExpiresAt(
    space.expiresAt,
    computeExpiresAt(
      getExpiresAfterDays(partner.app_settings, career?.app_settings)
    )
  )

  const { mailTemplate, mailTemplateMacros } = useMailTemplates({
    isCrossmode: true,
    expiresAt
  })

  const navigate = useNavigate()

  // D16, jeder Modus. Zu einer archivierten Stelle kann das Talent das
  // Assessment nicht mehr durchlaufen. Statt „Einladen" steht dann der Hinweis
  // mit dem Weg zum Wiederherstellen. Die Stelle wird beim Öffnen nachgeladen,
  // wenn sie in keinem Cache steht, bis dahin ist Einladen gesperrt.
  const { archivedCareerIds, pending: isCareerPending } = useArchivedCareerIds({
    spaces: [space],
    fetchWhen: open
  })
  const careerArchived = isSpaceOfArchivedCareer(space, archivedCareerIds)

  const reinviteSpace = () => {
    if (careerArchived || isCareerPending) return

    setState('IS_LOADING')

    const variables = {
      space_id: space.id,
      all_challenges_unlocked: true
    }

    if (context === 'EMAIL') {
      variables.email = space.email
      variables.language = space.language
      variables.career_id = space.career_id
      variables.partner_id = space.partner_id
      variables.email_template = 'unlock'
    }

    query({ query: 'inviteTalent', variables })
      .then(() => {
        updateHandler()
        space.all_challenges_unlocked = true

        if (context === 'EMAIL') setState('SUCCESS_VIA_EMAIL')
        else setState('SUCCESS_VIA_LINK')
      })
      .catch(() => setState('INITIAL'))
  }

  const handleClose = () => {
    setOpen(false)
    setTimeout(() => setState('INITIAL'), 0.4 * 1000)
  }

  return (
    <Modal open={open} setOpen={handleClose} size='2xl'>
      <h2 className='text-3xl font-extrabold tracking-tight text-gray-900'>
        {t('spaces.reinvite_space_modal.modal_title')}
      </h2>
      {['INITIAL', 'IS_LOADING'].includes(state) && careerArchived && (
        <div className='mt-4'>
          <InfoCallout tone='warning' className='mt-6 max-w-2xl'>
            {t(ARCHIVED_CAREER_HINTS[ARCHIVED_CAREER_ACTIONS.REINVITE])}
          </InfoCallout>
          <div className='mt-8 flex gap-x-4'>
            <Button.SecondaryXL
              fullWidth
              onClick={handleClose}
              text={t('spaces.reinvite_space_modal.cancel_action')}
            />
            <Button.PrimaryXL
              fullWidth
              text={t(ARCHIVED_CAREER_RESTORE)}
              onClick={() => {
                handleClose()
                navigate(RECOVER_CAREERS_PATH)
              }}
            />
          </div>
        </div>
      )}
      {['INITIAL', 'IS_LOADING'].includes(state) && !careerArchived && (
        <>
          <p className='mt-2 max-w-2xl text-sm text-gray-700'>
            {t('spaces.reinvite_space_modal.modal_description_1')}
          </p>
          <div className='mt-4'>
            <div className='mt-6 max-w-2xl'>
              <SelectMenu
                id='context'
                label={t(
                  'spaces.reinvite_space_modal.invite_via_select_menu_label'
                )}
                options={[
                  [
                    'EMAIL',
                    t('spaces.reinvite_space_modal.invite_via_email_option')
                  ],
                  [
                    'LINK',
                    t('spaces.reinvite_space_modal.invite_via_link_option')
                  ]
                ]}
                onChange={setContext}
                defaultValue={context}
                disabled={!space_has_email}
                translate={false}
              />
            </div>
            {context === 'LINK' && (
              <p className='mt-6 max-w-2xl text-sm italic text-gray-700'>
                {t('spaces.reinvite_space_modal.modal_description_2')}
              </p>
            )}
            {context === 'EMAIL' && (
              <div className='mt-6 flex max-w-2xl items-center justify-between'>
                <div>
                  <span className='block text-sm font-medium text-gray-700'>
                    {t('spaces.reinvite_space_modal.mail_template_label')}
                  </span>
                  <span className='mt-1 block text-sm font-semibold text-gray-900'>
                    {mailTemplate.subject}
                  </span>
                </div>
                <Button.SoftSM
                  text={t(
                    'spaces.reinvite_space_modal.show_preview_mail_template_action'
                  )}
                  onClick={() => setShowMailTemplatePreview(true)}
                />
              </div>
            )}
            <div className='mt-8 flex gap-x-4'>
              <Button.SecondaryXL
                fullWidth
                disabled={state === 'IS_LOADING'}
                onClick={handleClose}
                text={t('spaces.reinvite_space_modal.cancel_action')}
              />
              <Button.PrimaryXL
                fullWidth
                isLoading={state === 'IS_LOADING'}
                disabled={isCareerPending}
                text={t(
                  'spaces.reinvite_space_modal.' +
                    (context === 'EMAIL'
                      ? 'invite_action'
                      : 'invite_action_link')
                )}
                onClick={reinviteSpace}
              />
            </div>
          </div>
        </>
      )}
      {['SUCCESS_VIA_EMAIL', 'SUCCESS_VIA_LINK'].includes(state) && (
        <div className='mt-4'>
          <div className='flex items-center justify-between px-4'>
            <span className='block w-1/2 text-center text-sm text-gray-700'>
              {state === 'SUCCESS_VIA_EMAIL' &&
                t('spaces.reinvite_space_modal.success_via_email')}
              {state === 'SUCCESS_VIA_LINK' &&
                t('spaces.reinvite_space_modal.success_via_link')}
            </span>
            <img
              className='hidden w-64 sm:block'
              alt='Person Heart'
              src={PersonHeart}
            />
          </div>
          {state === 'SUCCESS_VIA_LINK' && (
            <div className='mt-8 px-8'>
              <SpaceInviteLink spaceID={space.id} language={space.language} />
            </div>
          )}
          <div className='mt-12 flex gap-x-4'>
            <Button.SecondaryXL
              fullWidth
              onClick={handleClose}
              text={t('spaces.reinvite_space_modal.done_action')}
            />
          </div>
        </div>
      )}
      <EditMailTemplate
        open={showMailTemplatePreview}
        setOpen={setShowMailTemplatePreview}
        mailTemplate={mailTemplate}
        mailTemplateMacros={mailTemplateMacros}
        recipient={{
          email: space.person?.email || space.person?.invitation_email,
          firstname:
            space.person?.firstname || space.person?.invitation_firstname,
          lastname: space.person?.lastname || space.person?.invitation_lastname
        }}
        preview
      />
    </Modal>
  )
}

ReinviteSpaceModal.propTypes = {
  space: PropTypes.object,
  updateHandler: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

export default ReinviteSpaceModal
